import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useForecast } from "../hooks/useForecast";

import { SegmentedControl } from "../components/SegmentedControl";
import { EmptyState } from "../components/EmptyState";
import { fadeUp, pop } from "../motion";
import { IconReminders } from "../components/icons";
import "../styles/forecast.css";

// Deadline forecast heatmap (Stage F): homes × fortnights over the next 4–12
// weeks. Counts are CURRENT next-deadlines only — future 1m→6m→1y cycles are
// NOT simulated (said so in the UI, so the grid never promises false precision).



const FORTNIGHT_DAYS = 14;
const WEEK_OPTIONS = [
  { value: "4", label: "4 weeks" },
  { value: "8", label: "8 weeks" },
  { value: "12", label: "12 weeks" },
] as const;

const DAY_MS = 24 * 3600 * 1000;

function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + days)).toISOString().slice(0, 10);
}
function fmt(ymd: string): string {
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(new Date(`${ymd}T00:00:00Z`));
}

function fmtRange(start: string, end: string): string {
  return `${fmt(start)} – ${fmt(end)}`;
}

/** Cell intensity bucket (0 = empty … 3 = heavy). */
function intensity(count: number): number {
  if (count === 0) return 0;
  if (count <= 2) return 1;
  if (count <= 5) return 2;
  return 3;
}

interface Selection {
  homeId: string;
  fortnight: number; // index; -1 = overdue column
}

export function ForecastPage() {
  const [weeks, setWeeks] = useState<string>("8");
  const [sel, setSel] = useState<Selection | null>(null);
  const query = useForecast(weeks);
  const data = query.data;

  const fortnightCount = useMemo(() => (data ? Math.ceil((data.weeks * 7) / FORTNIGHT_DAYS) : 0), [data]);
  const fortnightOf = useMemo(() => (date: string): number => {
    if (!data) return 0;
    const from = new Date(`${data.start}T00:00:00Z`).getTime();
    const to = new Date(`${date}T00:00:00Z`).getTime();
    return Math.floor((to - from) / (FORTNIGHT_DAYS * DAY_MS));
  }, [data]);

  const selectedResidents = useMemo(() => {
    if (!sel || !data) return [];
    const row = data.rows.find((r) => r.homeId === sel.homeId);
    if (!row) return [];
    if (sel.fortnight === -1) return row.overdue;
    return row.cells.filter((c) => fortnightOf(c.date) === sel.fortnight).flatMap((c) => c.residents).sort((a, b) => a.deadline.localeCompare(b.deadline));
  }, [sel, data, fortnightOf]);

  const selectedRow = sel ? data?.rows.find((r) => r.homeId === sel.homeId) : undefined;

  return (
    <>
      <motion.div className="page-head" variants={fadeUp} initial="hidden" animate="show">
        <h1>Forecast</h1>
      </motion.div>

      <motion.div className="toolbar" variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.05 }}>
        <SegmentedControl ariaLabel="Forecast horizon" layoutId="forecast-weeks" options={[...WEEK_OPTIONS]} value={weeks} onChange={setWeeks} />
        <Link className="btn btn-ghost" to="/todos">← To-dos</Link>
      </motion.div>

      {query.isLoading ? <p className="muted">Loading…</p> : null}
      {query.isError ? <p className="error">Failed to load the forecast. Retry.</p> : null}

      {data ? (
        data.rows.length === 0 ? (
          <motion.div variants={pop} initial="hidden" animate="show">
            <EmptyState icon={<IconReminders />} title="No deadlines in this window" hint={`Nothing due between ${fmt(data.start)} and ${fmt(data.end)}.`} />
          </motion.div>
        ) : (
          <motion.div className="forecast" variants={fadeUp} initial="hidden" animate="show" role="table" aria-label="Deadline forecast">
            <div className="forecast-head forecast-grid" style={{ ["--cols" as string]: fortnightCount }} role="row">
              <span className="forecast-home-head" role="columnheader">Home</span>
              <span className="forecast-col-head" role="columnheader">Overdue</span>
              {Array.from({ length: fortnightCount }, (_, i) => (
                <span key={i} className="forecast-col-head" role="columnheader">
                  {fmtRange(addDays(data.start, i * FORTNIGHT_DAYS), addDays(data.start, (i + 1) * FORTNIGHT_DAYS - 1))}
                </span>
              ))}
            </div>
            {data.rows.map((row) => (
              <div key={row.homeId} className="forecast-grid" role="row" style={{ ["--cols" as string]: fortnightCount }}>
                <span className="forecast-home" role="rowheader">{row.homeName}</span>
                <button
                  type="button"
                  role="cell"
                  className={`forecast-cell forecast-overdue ${sel?.homeId === row.homeId && sel.fortnight === -1 ? "selected" : ""}`}
                  aria-label={`${row.homeName}: ${row.overdue.length} overdue`}
                  onClick={() => setSel(sel?.homeId === row.homeId && sel.fortnight === -1 ? null : { homeId: row.homeId, fortnight: -1 })}
                >
                  {row.overdue.length > 0 ? row.overdue.length : ""}
                </button>
                {Array.from({ length: fortnightCount }, (_, i) => {
                  const count = row.cells.filter((c) => fortnightOf(c.date) === i).reduce((n, c) => n + c.residents.length, 0);
                  return (
                    <button
                      key={i}
                      type="button"
                      role="cell"
                      aria-label={`${row.homeName}: ${count} due ${fmtRange(addDays(data.start, i * FORTNIGHT_DAYS), addDays(data.start, (i + 1) * FORTNIGHT_DAYS - 1))}`}
                      className={`forecast-cell forecast-i${intensity(count)} ${sel?.homeId === row.homeId && sel.fortnight === i ? "selected" : ""}`}
                      onClick={() => setSel(sel?.homeId === row.homeId && sel.fortnight === i ? null : { homeId: row.homeId, fortnight: i })}
                    >
                      {count > 0 ? count : ""}
                    </button>
                  );
                })}
              </div>
            ))}
          </motion.div>
        )
      ) : null}

      {sel && selectedRow ? (
        <motion.section className="widget" variants={fadeUp} initial="hidden" animate="show">
          <header className="widget-head">
            <h2 className="widget-title">
              {selectedRow.homeName} · {sel.fortnight === -1 ? "overdue" : `${fmtRange(addDays(data!.start, sel.fortnight * FORTNIGHT_DAYS), addDays(data!.start, (sel.fortnight + 1) * FORTNIGHT_DAYS - 1))}`}
            </h2>
          </header>
          <div className="widget-body">
            {selectedResidents.length === 0 ? (
              <p className="muted">No residents due in this window.</p>
            ) : (
              <ul className="mini-list forecast-residents">
                {selectedResidents.map((r) => (
                  <li key={r.id} className="mini-row">
                    <div className="mini-main">
                      <p className="mini-title">{r.name} {r.bed ? <span className="muted">· {r.bed}</span> : null}</p>
                    </div>
                    <span className={`chip ${r.overdue ? "chip-danger" : "chip-warn"}`}>ICP {r.deadline}</span>
                    <Link className="widget-link" to={`/residents/${r.id}`}>View →</Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.section>
      ) : null}
    </>
  );
}
